import React, { useEffect, useState } from 'react';
import { Tag, Clock, X, ChevronLeft, ChevronRight } from 'lucide-react';
import apiClient from '@/utils/api';

/**
 * Promotional banner strip (V5.1).
 *
 * Fixes applied:
 *  - Fixed positioning (top-16 = just below navbar), body padding compensated dynamically.
 *  - businessUnit prop is now REQUIRED from the caller (App.js supplies it via route mapping).
 *    When businessUnit is "__none__" or an unmatched value, no banners are shown.
 *  - Multiple simultaneous banners rotate every 5 s with prev/next controls.
 *  - Dismissal persists per session (sessionStorage).
 *
 * Props:
 *  - businessUnit (string): route-derived business unit. Pass "__none__" to suppress.
 */
export default function PromoBanner({ businessUnit, userType }) {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(
    () => JSON.parse(sessionStorage.getItem('zenstudios_dismissed_banners') || '[]')
  );
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!businessUnit || businessUnit === '__none__') return;
    const params = { business_unit: businessUnit };
    if (userType) params.user_type = userType;
    apiClient
      .get('/coupons/banners', { params })
      .then(({ data }) => setBanners(data.banners || []))
      .catch(() => {});
  }, [businessUnit, userType]);

  // Rotation: cycle every 5 s when multiple visible banners
  const visible = banners.filter((b) => !dismissed.includes(b.code));

  useEffect(() => {
    if (visible.length <= 1) return;
    setIdx(0); // reset index when banner set changes
    const t = setInterval(() => setIdx((i) => (i + 1) % visible.length), 5000);
    return () => clearInterval(t);
  }, [visible.length]);

  // Fixed positioning: push body content down by banner height when visible
  useEffect(() => {
    const bannerHeight = visible.length > 0 ? 44 : 0;
    document.body.style.paddingTop = `${bannerHeight}px`;
    return () => { document.body.style.paddingTop = ''; };
  }, [visible.length]);

  if (visible.length === 0) return null;

  const b = visible[Math.min(idx, visible.length - 1)];

  const dismiss = (code) => {
    const next = [...dismissed, code];
    setDismissed(next);
    sessionStorage.setItem('zenstudios_dismissed_banners', JSON.stringify(next));
    setIdx(0);
  };

  return (
    <div
      data-testid="promo-banner"
      className="fixed top-16 left-0 right-0 z-40 flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 h-11 text-sm shadow-sm"
    >
      <Tag size={15} className="shrink-0" />
      <span className="font-semibold truncate">{b.banner_title}</span>
      {b.banner_text && (
        <span className="hidden sm:inline opacity-90 truncate">— {b.banner_text}</span>
      )}
      {/* Auto-apply promotions need no code — show "Applied automatically" instead of a code chip */}
      {b.auto_apply ? (
        <span className="font-medium bg-white/20 rounded px-2 py-0.5 text-xs shrink-0" data-testid="promo-auto-applied">
          Applied automatically
        </span>
      ) : (
        <span className="font-mono bg-white/20 rounded px-2 py-0.5 text-xs shrink-0">{b.code}</span>
      )}
      {b.status === 'expiring_soon' && b.days_left != null && (
        <span className="hidden md:flex items-center gap-1 text-xs bg-white/20 rounded px-2 py-0.5 shrink-0">
          <Clock size={11} /> {b.days_left}d left
        </span>
      )}

      {/* Multi-banner nav (only when > 1) */}
      {visible.length > 1 && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setIdx((i) => (i - 1 + visible.length) % visible.length)}
            className="p-0.5 rounded opacity-80 hover:opacity-100"
            aria-label="Previous offer"
            data-testid="promo-banner-prev"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-[10px] opacity-70">{idx + 1}/{visible.length}</span>
          <button
            onClick={() => setIdx((i) => (i + 1) % visible.length)}
            className="p-0.5 rounded opacity-80 hover:opacity-100"
            aria-label="Next offer"
            data-testid="promo-banner-next"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      <button
        onClick={() => dismiss(b.code)}
        data-testid="promo-banner-dismiss"
        className="absolute right-3 opacity-80 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}
